/**
 * Google Business Profile exposes one `comment` string. Some reviews contain
 * both the author's original and a Google-produced translation in that string.
 * Display must never mistake Google's translation for the customer's own words.
 *
 * The supplied translation's destination language is NOT authoritative. Keep
 * it secondary until an explicit business-locale translation provider exists.
 * Do not alter stored review text, analysis, generated drafts, or Google data.
 */
export interface GoogleReviewTextParts {
  original: string;
  googleTranslation: string | null;
}

const GOOGLE_MARKER = /\((Translated by Google|Traducido por Google|Traduit par Google|Traduzido pelo Google|Original|Texto original|Texte original|Texto original)\)/gi;

function isTranslationMarker(label: string): boolean {
  return /^(translated by google|traducido por google|traduit par google|traduzido pelo google)$/i.test(label);
}

export function splitGoogleReviewText(text: string | null | undefined): GoogleReviewTextParts {
  const raw = typeof text === "string" ? text.trim() : "";
  if (!raw) return { original: "", googleTranslation: null };
  const matches = Array.from(raw.matchAll(GOOGLE_MARKER));
  const translations = matches.filter((match) => isTranslationMarker(match[1] || ""));
  if (translations.length !== 1) return { original: raw, googleTranslation: null };

  const translated = translations[0]!;
  const originalLabels = matches.filter((match) => !isTranslationMarker(match[1] || ""));
  if (originalLabels.length > 1) return { original: raw, googleTranslation: null };

  // Google may provide "(Original)" before or after the translated segment.
  if (originalLabels.length === 1) {
    const originalMarker = originalLabels[0]!;
    const originalFirst = (originalMarker.index ?? 0) < (translated.index ?? 0);
    const first = originalFirst ? originalMarker : translated;
    const second = originalFirst ? translated : originalMarker;
    const firstBody = raw.slice((first.index ?? 0) + first[0].length, second.index).trim();
    const secondBody = raw.slice((second.index ?? 0) + second[0].length).trim();
    const original = originalFirst ? firstBody : secondBody;
    const googleTranslation = originalFirst ? secondBody : firstBody;
    if (original && googleTranslation) return { original, googleTranslation };
    return { original: raw, googleTranslation: null };
  }

  // Some Google responses omit "(Original)" and prepend the author's message.
  // Never guess if the translated marker is at the beginning.
  if ((translated.index ?? 0) > 0) {
    const original = raw.slice(0, translated.index).trim();
    const googleTranslation = raw.slice((translated.index ?? 0) + translated[0].length).trim();
    if (original && googleTranslation) return { original, googleTranslation };
  }
  return { original: raw, googleTranslation: null };
}

export function originalReviewText(text: string | null | undefined): string {
  return splitGoogleReviewText(text).original;
}
